import React from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import AIPanel from "@/components/AIPanel";
import CommentsDrawer from "@/components/CommentsDrawer";
import { PresenceAvatars } from "@/components/PresenceAvatars";
import { useProjectRealtime } from "@/hooks/useProjectRealtime";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, RefreshCw, Loader2, MessageSquare, Users, FileDown } from "lucide-react";
import { logger } from "@/lib/logger";

const TYPES = [
  { key: "scene_heading", label: "Scene", shortcut: "⌘1" },
  { key: "action", label: "Action", shortcut: "⌘2" },
  { key: "character", label: "Character", shortcut: "⌘3" },
  { key: "parenthetical", label: "Paren.", shortcut: "⌘4" },
  { key: "dialogue", label: "Dialogue", shortcut: "⌘5" },
  { key: "transition", label: "Transition", shortcut: "⌘6" },
];

const nextType = (t) => {
  const map = {
    scene_heading: "action",
    action: "action",
    character: "dialogue",
    parenthetical: "dialogue",
    dialogue: "character",
    transition: "scene_heading",
  };
  return map[t] || "action";
};

export default function ScriptEditor() {
  const { projectId } = useParams();
  const [project, setProject] = React.useState(null);
  const [nodes, setNodes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [selectedSceneId, setSelectedSceneId] = React.useState(null);
  const [selectionText, setSelectionText] = React.useState("");
  const [focusedIdx, setFocusedIdx] = React.useState(-1);
  const [commentsOpen, setCommentsOpen] = React.useState(false);
  const inputRefs = React.useRef([]);
  const rt = useProjectRealtime(projectId);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/script`),
      ]);
      setProject(p.data);
      setNodes(s.data);
      setDirty(false);
    } catch (e) {
      toast.error("Could not load screenplay");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const save = React.useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        nodes: nodes.map((n, i) => ({
          type: n.type,
          content: n.content,
          order: (i + 1) * 1000,
          scene_id: n.scene_id || null,
          character_id: null,
        })),
      };
      await api.put(`/projects/${projectId}/script/bulk`, payload);
      setDirty(false);
      // Reload to get server-assigned scene_ids
      const s = await api.get(`/projects/${projectId}/script`);
      setNodes(s.data);
    } catch (err) {
      logger.warn("script editor: save failed", err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [nodes, projectId]);

  // Auto-save when dirty (debounced)
  React.useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => save(), 1500);
    return () => clearTimeout(t);
  }, [dirty, save]);


  const updateNode = (idx, patch) => {
    setNodes((ns) => ns.map((n, i) => (i === idx ? { ...n, ...patch } : n)));
    setDirty(true);
  };

  const insertNode = (idx, type = "action") => {
    const newNode = {
      id: `local-${Date.now()}-${Math.random()}`,
      type, content: "", scene_id: null,
    };
    setNodes((ns) => [...ns.slice(0, idx + 1), newNode, ...ns.slice(idx + 1)]);
    setDirty(true);
    setTimeout(() => {
      const el = inputRefs.current[idx + 1];
      if (el) el.focus();
    }, 30);
  };

  const removeNode = (idx) => {
    setNodes((ns) => ns.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleKey = (e, idx, node) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      insertNode(idx, nextType(node.type));
      return;
    }
    if (e.key === "Backspace" && node.content === "" && nodes.length > 1) {
      e.preventDefault();
      removeNode(idx);
      setTimeout(() => inputRefs.current[Math.max(0, idx - 1)]?.focus(), 0);
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const curIdx = TYPES.findIndex((t) => t.key === node.type);
      const nextT = TYPES[(curIdx + 1) % TYPES.length].key;
      updateNode(idx, { type: nextT });
      return;
    }
    // Type hotkeys
    if ((e.metaKey || e.ctrlKey) && "123456".includes(e.key)) {
      e.preventDefault();
      const idx2 = parseInt(e.key, 10) - 1;
      if (TYPES[idx2]) updateNode(idx, { type: TYPES[idx2].key });
    }
  };

  const handleSelect = (idx) => {
    const el = inputRefs.current[idx];
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    if (s !== e) setSelectionText(el.value.substring(s, e));
    rt.sendCursor(idx, { start: s, end: e });
  };

  const openAIWithScene = (idx) => {
    // Find enclosing scene_id by walking backwards
    let sid = null;
    for (let i = idx; i >= 0; i--) {
      if (nodes[i].type === "scene_heading") {
        sid = nodes[i].scene_id;
        break;
      }
    }
    setSelectedSceneId(sid);
    if (!selectionText) {
      const node = nodes[idx];
      setSelectionText(node?.content || "");
    }
    setAiOpen(true);
  };

  const insertAIOutput = (text) => {
    // Parse the returned screenplay text into nodes (heuristic)
    const parsed = parseScreenplayText(text);
    if (parsed.length === 0) {
      // fallback: single action
      insertNode(focusedIdx >= 0 ? focusedIdx : nodes.length - 1, "action");
      updateNode((focusedIdx >= 0 ? focusedIdx : nodes.length - 1) + 1, { content: text });
    } else {
      const insertAt = focusedIdx >= 0 ? focusedIdx + 1 : nodes.length;
      const newOnes = parsed.map((p, i) => ({
        id: `local-${Date.now()}-${i}`, type: p.type, content: p.content, scene_id: null,
      }));
      setNodes((ns) => [...ns.slice(0, insertAt), ...newOnes, ...ns.slice(insertAt)]);
      setDirty(true);
    }
    setAiOpen(false);
    toast.success("Inserted");
  };

  if (loading) return <DashboardLayout><div className="p-10 text-[#A1A1AA]">Loading screenplay…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-4rem)]">
        <div className="flex-1 flex flex-col min-w-0">
          {/* Editor toolbar */}
          <div className="h-12 border-b border-[#1E1E1E] bg-[#0F0F10] flex items-center justify-between px-4 gap-3 shrink-0">
            <div className="flex items-center gap-2 text-xs">
              <div className="font-editorial text-lg mr-3">{project?.title}</div>
              {TYPES.map((t) => (
                <button key={t.key}
                  onClick={() => focusedIdx >= 0 && updateNode(focusedIdx, { type: t.key })}
                  className="px-2.5 py-1 border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                  title={t.shortcut}
                  data-testid={`toolbar-type-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <PresenceAvatars peers={rt.peers} selfInfo={rt.selfInfo} />
              <span className="text-[10px] uppercase tracking-widest text-[#71717A]" data-testid="editor-save-state">
                {saving ? "Saving…" : dirty ? "Unsaved" : "Saved"}
              </span>
              <button onClick={save} className="text-[#A1A1AA] hover:text-[#FFFFF0] p-1.5" title="Save" data-testid="editor-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </button>
              <button onClick={load} className="text-[#A1A1AA] hover:text-[#FFFFF0] p-1.5" title="Reload" data-testid="editor-reload">
                <RefreshCw size={14} />
              </button>
              <button onClick={() => downloadPitchDeck(projectId, project?.title)} className="text-[#A1A1AA] hover:text-[#FFFFF0] p-1.5" title="Export pitch deck (PDF)" data-testid="editor-pitch-deck-btn">
                <FileDown size={14} />
              </button>
              <button onClick={() => setCommentsOpen(!commentsOpen)} className="text-[#A1A1AA] hover:text-[#FFFFF0] p-1.5 relative" title="Comments" data-testid="editor-comments-btn">
                <MessageSquare size={14} />
                {rt.comments.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#D4AF37] rounded-full"></span>}
              </button>
              <button
                onClick={() => setAiOpen(!aiOpen)}
                className="bg-[#D4AF37] text-[#0A0A0A] px-3 py-1.5 text-xs font-medium hover:bg-[#F59E0B] inline-flex items-center gap-1.5 transition-colors"
                data-testid="editor-open-ai">
                <Sparkles size={12} /> AI Writer
              </button>
            </div>
          </div>

          {/* Editor canvas */}
          <div className="flex-1 overflow-y-auto bg-[#0A0A0A]" data-testid="script-editor-canvas">
            <div className="max-w-4xl mx-auto my-8 sf-editor-page shadow-2xl">
              {nodes.length === 0 && (
                <div className="text-[#71717A] text-sm italic text-center py-20">
                  Empty page. Press <kbd className="text-[#D4AF37]">Enter</kbd> to start writing.
                  <div className="mt-4">
                    <button onClick={() => insertNode(-1, "scene_heading")} className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 text-xs font-medium">
                      Add first scene
                    </button>
                  </div>
                </div>
              )}
              {nodes.map((n, i) => (
                <div key={n.id} className="relative group">
                  <textarea
                    ref={(el) => (inputRefs.current[i] = el)}
                    value={n.content}
                    onChange={(e) => updateNode(i, { content: e.target.value })}
                    onKeyDown={(e) => handleKey(e, i, n)}
                    onFocus={() => setFocusedIdx(i)}
                    onSelect={() => handleSelect(i)}
                    rows={1}
                    className={`sf-node w-full bg-transparent border-none outline-none resize-none font-mono-editor text-[15px] leading-[1.65] py-0.5`}
                    data-type={n.type}
                    data-testid={`script-node-${i}`}
                    placeholder={placeholderFor(n.type)}
                    style={{ minHeight: '1.7em' }}
                    onInput={(e) => {
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                  />
                  {focusedIdx === i && (
                    <div className="absolute right-full mr-3 top-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openAIWithScene(i)}
                        className="w-6 h-6 border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                        title="Ask AI"
                        data-testid={`node-ai-btn-${i}`}>
                        <Sparkles size={10} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {nodes.length > 0 && (
                <button onClick={() => insertNode(nodes.length - 1, "action")}
                  className="mt-8 text-[#71717A] hover:text-[#D4AF37] text-xs uppercase tracking-widest"
                  data-testid="add-node-btn">
                  + Add line
                </button>
              )}
            </div>
          </div>
        </div>

        <AIPanel
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          projectId={projectId}
          sceneId={selectedSceneId}
          selectionText={selectionText}
          onInsert={insertAIOutput}
        />
        <CommentsDrawer
          projectId={projectId}
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          sendComment={rt.sendComment}
          liveComments={rt.comments}
          setLiveComments={rt.setComments}
        />
      </div>
    </DashboardLayout>
  );
}

function placeholderFor(type) {
  return {
    scene_heading: "INT. LOCATION — DAY",
    action: "Describe what we see.",
    character: "CHARACTER NAME",
    parenthetical: "(beat)",
    dialogue: "What the character says.",
    transition: "CUT TO:",
    shot: "ANGLE ON —",
    note: "// Note",
  }[type] || "";
}

async function downloadPitchDeck(projectId, title) {
  const token = localStorage.getItem("sf_token");
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/pitch-deck.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    toast.error("Could not generate PDF");
    return;
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(title || "storyframe").replace(/\s+/g, "_")}_pitch.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success("Pitch deck downloaded");
}

const HEADING_RE = /^\s*(INT|EXT|I\/E|INT\/EXT)\.?\s+/i;
const TRANSITION_RE = /^(CUT TO:|FADE IN:|FADE OUT\.?|SMASH CUT:|DISSOLVE TO:|MATCH CUT:)/i;

function parseScreenplayText(text) {
  const lines = text.split(/\r?\n/);
  const out = [];
  for (let raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (HEADING_RE.test(line)) {
      out.push({ type: "scene_heading", content: line });
    } else if (TRANSITION_RE.test(line)) {
      out.push({ type: "transition", content: line });
    } else if (/^[A-Z][A-Z0-9 .'\-()]{1,30}$/.test(line) && line === line.toUpperCase() && line.length < 32) {
      out.push({ type: "character", content: line });
    } else if (/^\(.+\)$/.test(line)) {
      out.push({ type: "parenthetical", content: line });
    } else if (out.length && (out[out.length - 1].type === "character" || out[out.length - 1].type === "parenthetical")) {
      out.push({ type: "dialogue", content: line });
    } else {
      out.push({ type: "action", content: line });
    }
  }
  return out;
}
